import { Injectable } from '@angular/core';
import {
  HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Router } from '@angular/router';

/**
 * "You're trying to record something after your service ended."
 *
 * The server refuses a record dated past the client's paid coverage with a 423
 * and `code: 'beyond_coverage'`. That refusal could be surfaced in each of the
 * thirty-odd posting screens, or once here. Once here — because the screens
 * that need it are exactly the screens nobody remembers to update, and a
 * missing case shows up as a raw error toast reading like a crash.
 *
 * This is a SOFT block by design. The client keeps everything up to their
 * coverage date: reading, exporting, drafting reports, and posting or
 * correcting records inside the period they paid for. The only thing refused
 * is dating a record after the engagement ended — and the dialog says so
 * plainly and offers the one action that fixes it.
 *
 * Deliberately NOT a redirect. Bouncing someone to a renewal page mid-entry
 * loses whatever they had typed, and "your session ended, start again" is how a
 * pricing conversation turns into a support ticket.
 */
@Injectable()
export class CoverageInterceptor implements HttpInterceptor {

  /** One dialog at a time — a batch post can fail on several rows at once. */
  private showing = false;

  constructor(
    private modal: NzModalService,
    private router: Router
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status === 423) {
          const body: any = err.error || {};
          const message = String(body?.message ?? body?.error ?? '');

          // Recognised three ways, because the ~25 posting routes each have a
          // hand-written catch block and most propagate only the status and
          // the message — `code` and `covered_until` are dropped by all but a
          // handful. Keying on `code` alone meant the dialog fired on four
          // routes and the other twenty showed a raw error toast.
          //
          // COVERAGE_MARKER is the server's own phrase, pinned by a test on
          // both sides so the wording cannot drift apart from this check.
          const isCoverage = body?.code === 'beyond_coverage'
            || !!body?.covered_until
            || message.includes(CoverageInterceptor.COVERAGE_MARKER);

          if (isCoverage) {
            this.warn(body?.covered_until || this.dateFromMessage(message));
          } else {
            // The other 423 is the admin period lock — same "wrong date"
            // shape, different remedy. Renewing would not help, so it gets a
            // plain explanation rather than a sales button.
            this.explainLock(message);
          }
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * Must match `COVERAGE_MARKER` in lib/accountingCoverage.js.
   * scripts/verifyCoverageGate.js asserts the two agree.
   */
  static readonly COVERAGE_MARKER = 'covers records up to';

  /** Recover the date from the message when the field did not survive. */
  private dateFromMessage(message: string): string | null {
    const m = /(\d{4}-\d{2}-\d{2})/.exec(message);
    return m ? m[1] : null;
  }

  private explainLock(message: string): void {
    if (this.showing) return;
    this.showing = true;
    this.modal.info({
      nzTitle: 'This period is closed',
      nzContent: message || 'This accounting period has been locked and cannot be changed.',
      nzOkText: 'Got it',
      nzOnOk: () => { this.showing = false; }
    });
  }

  private warn(coveredUntil: string | null): void {
    if (this.showing) return;
    this.showing = true;

    const until = coveredUntil
      ? `<strong>${coveredUntil}</strong>`
      : 'the end of your last term';

    this.modal.confirm({
      nzTitle: 'This date is outside your service period',
      nzContent:
        `<p>Your bookkeeping service covers records up to ${until}, `
        + `and this entry is dated after that.</p>`
        + `<p>Everything up to that date is still fully yours &mdash; you can keep `
        + `viewing, exporting, drafting reports and correcting records inside it. `
        + `To record something after it, renew your service.</p>`,
      nzOkText: 'Renew my plan',
      nzCancelText: 'Not now',
      nzWidth: 480,
      nzOnOk: () => {
        this.showing = false;
        this.router.navigateByUrl('/accounting/activate/plan');
      },
      // "Not now" is a real answer. They stay exactly where they are, with
      // their work intact, and can change the date instead.
      nzOnCancel: () => { this.showing = false; }
    });
  }
}
